// @catalog F.030
// Helpers de formato/estado del dashboard /margenes — etiquetas de meses, orden,
// claves ISO, período por default y selección del mes actual. Extraídos de
// MarginsLevel1.tsx para respetar P7 (≤500 líneas/archivo).
import { type PeriodKey } from "../analytics/PeriodSelector";
import {
  type MonthlyMarginsResponse,
  type StoreMarginMetrics,
} from "../../services/margins.service";
import { type RankingItem } from "../analytics/RankingBars";

export const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export const MONTH_LABELS_FULL = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export function monthOrder(name: string): number {
  const idx = MONTH_NAMES.indexOf(name.toLowerCase());
  return idx === -1 ? 0 : idx;
}

export function isoToMonthKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}`;
}

export function clampToCurrentMonth(monthKey: string | null): string | null {
  if (!monthKey) return null;
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  return monthKey > currentKey ? currentKey : monthKey;
}

// /margenes arranca SIEMPRE en el mes en curso (MTD = primer día del mes actual a
// hoy). El usuario puede cambiar el período durante su visita, pero al reabrir el
// módulo vuelve a mostrar el mes en curso por default.
export function defaultPeriod(): PeriodKey {
  return "mtd";
}

export function pickCurrent(
  data: MonthlyMarginsResponse[],
): MonthlyMarginsResponse | null {
  if (data.length === 0) return null;
  const sorted = [...data].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return monthOrder(a.month) - monthOrder(b.month);
  });
  return sorted[sorted.length - 1] ?? null;
}

export interface PeriodMarginAggregate {
  marginAmount: number;
  revenue: number;
  cost: number;
  discounts: number;
  commissions: number;
  marginPct: number;
  // Variación del margen % del período vs el mismo período del año anterior (pp).
  marginPctDeltaPp: number | null;
  monthsCount: number;
}

// Agrega los meses del rango filtrado (monthlyData ya viene filtrado por el
// backend) en un único set de KPIs del PERÍODO. El margen % se recalcula como
// Σmargen/Σingreso (NO promedia porcentajes). El delta YoY deriva el ingreso del
// año anterior por mes (margen$ ÷ (margen%/100)) porque el backend solo expone
// previousYearMarginAmount/Percentage, no el ingreso previo directo.
export function aggregatePeriodMargins(
  data: MonthlyMarginsResponse[],
): PeriodMarginAggregate {
  let marginAmount = 0;
  let revenue = 0;
  let discounts = 0;
  let commissions = 0;
  let prevMargin = 0;
  let prevRevenue = 0;
  let hasPrev = false;
  for (const m of data) {
    marginAmount += m.totalMarginAmount ?? 0;
    revenue += m.totalRevenue ?? 0;
    discounts += m.totalDiscounts ?? 0;
    commissions += m.totalCommissions ?? 0;
    const pyMargin = m.previousYearMarginAmount;
    const pyPct = m.previousYearMarginPercentage;
    if (pyMargin !== undefined && pyPct !== undefined && pyPct > 0) {
      prevMargin += pyMargin;
      prevRevenue += pyMargin / (pyPct / 100);
      hasPrev = true;
    }
  }
  const marginPct = revenue > 0 ? (marginAmount / revenue) * 100 : 0;
  const prevPct =
    hasPrev && prevRevenue > 0 ? (prevMargin / prevRevenue) * 100 : null;
  return {
    marginAmount,
    revenue,
    cost: revenue - marginAmount,
    discounts,
    commissions,
    marginPct,
    marginPctDeltaPp: prevPct !== null ? marginPct - prevPct : null,
    monthsCount: data.length,
  };
}

// Desplaza un mes-clave (YYYY-MM) en N años (ej. -1 = mismo mes del año anterior).
export function shiftMonthKeyYear(
  monthKey: string | null,
  deltaYears: number,
): string | null {
  if (!monthKey) return null;
  const m = monthKey.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  return `${Number(m[1]) + deltaYears}-${m[2]}`;
}

// Mapea las tiendas a RankingItem para "Margen % por punto de venta". El delta
// (chip) es la variación del margen % vs el mismo período del año anterior en
// puntos porcentuales (prevMarginByStore); null si la tienda no tiene base previa
// → el chip se oculta en vez de mostrar un 0 engañoso.
export function buildMarginRankItems(
  stores: StoreMarginMetrics[],
  prevMarginByStore: Map<string, number>,
): RankingItem<StoreMarginMetrics>[] {
  return stores
    .map((s) => {
      const prev = prevMarginByStore.get(s.storeName);
      return {
        id: s.storeName,
        name: s.storeName,
        value: s.avgMarginPercentage,
        delta:
          prev !== undefined
            ? Number((s.avgMarginPercentage - prev).toFixed(2))
            : null,
        tag:
          s.avgMarginPercentage < 22
            ? "Bajo"
            : s.avgMarginPercentage > 34
              ? "Top"
              : null,
        tagStyle:
          s.avgMarginPercentage < 22
            ? ("alert" as const)
            : s.avgMarginPercentage > 34
              ? ("good" as const)
              : ("neutral" as const),
        raw: s,
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
}
